/**
 * Sdílený kontrakt automatizací (klient i server). Žádné server/klient importy —
 * jen čisté typy. Registr uzlů (registry.ts) a validace (validateGraph.ts) na tom staví.
 *
 * Viz docs/plans/1-p-e-ti-si-instrukce-witty-seahorse.md.
 */

/**
 * Typ hrany. `pulse` = jednorázová událost (v UI „Událost"), ostatní = kontinuální
 * signál. `pulse` a `boolean` jsou navzájem převoditelné oběma směry —
 * viz `typesCompatible` v registry.ts a koerce v interpreteru.
 */
export type DataType = "number" | "boolean" | "string" | "any" | "pulse";

/** Kategorie uzlu (řídí barvu + sekci v paletě editoru). */
export type NodeCategory = "trigger" | "value" | "logic" | "math" | "time" | "flow" | "action";

/**
 * Všechny typy uzlů (MVP fáze 1). Registr (registry.ts) je musí všechny pokrýt.
 * F2/F3 uzly se přidají sem + do registru, až na ně dojde.
 */
export const AUTOMATION_NODE_TYPES = [
  // A) Spouštěče (emitují pulz)
  "trigger.topicChange",
  "trigger.time",
  "trigger.interval",
  "trigger.manual",
  // B) Hodnoty (signálové zdroje)
  "value.topic",
  "value.lastDb",
  "value.const",
  "value.age",
  // C) Logika
  "logic.compare",
  "logic.and",
  "logic.or",
  "logic.not",
  "logic.hysteresis",
  // D) Matematika
  "math.op",
  "math.deltaOverTime",
  "math.formula",
  // E) Čas / tok
  "time.sustained",
  "time.window",
  "time.gate",
  "flow.risingEdge",
  "flow.throttle",
  "time.delay",
  // F) Akce (pulzní sinky)
  "action.notify",
  "action.log",
  // F2) Akce nad zařízeními (privilegované — ADMIN; zápis do HW / MQTT)
  "action.setPipParam",
  "action.setTracerParam",
  "action.setBmsParam",
  "action.publishMqtt",
] as const;

export type AutomationNodeType = (typeof AUTOMATION_NODE_TYPES)[number];

/** Specifikace jednoho portu (handle) uzlu. */
export type HandleSpec = {
  /** Id handlu, unikátní v rámci jedné strany uzlu (vstupy zvlášť, výstupy zvlášť). */
  id: string;
  dataType: DataType;
  /** Klíč do dictionary pro popisek (UI). */
  labelKey?: string;
  /** Variadický vstup (and/or) — počet handlů se generuje z params (resolveNodeHandles). */
  variadic?: boolean;
  /** Override max počtu hran do tohoto handlu (default: signál 1, pulz ∞). */
  maxEdges?: number;
  /**
   * Vstup smí zůstat nezapojený. Default (undefined) = POVINNÝ — validace hlásí
   * INPUT_NOT_CONNECTED. Nepovinné jsou jen dekorativní / parametrem nahraditelné vstupy.
   * U výstupů se ignoruje.
   */
  optional?: boolean;
  /** Doplňující věta do tooltipu portu (např. pravidlo „aspoň dva zapojené"). */
  hint?: string;
};

/** Jeden uzel grafu. `params` validuje per-type zod schéma z registru. */
export type AutomationNode = {
  /** Unikátní id v grafu (nanoid/uuid). */
  id: string;
  type: AutomationNodeType;
  params: Record<string, unknown>;
  position: { x: number; y: number };
  /** Volitelný uživatelský popisek. */
  label?: string;
  /** Interní metadata (např. legacySeed=true u notify z migrace). */
  meta?: Record<string, unknown>;
};

/** Hrana grafu (orientovaná source→target přes konkrétní handly). */
export type AutomationEdge = {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
};

/** Serializovaný graf automatizace (uložený v Automation.graph). */
export type AutomationGraph = {
  /** Bump při breaking změně; server umí migrovat při čtení. */
  schemaVersion: 1;
  nodes: AutomationNode[];
  edges: AutomationEdge[];
};

/** Deklarativní záznam typu uzlu v registru (registry.ts). */
export type NodeRegistryEntry = {
  type: AutomationNodeType;
  category: NodeCategory;
  /** Statické vstupy. Dynamické (variadic / pojmenované) řeší resolveNodeHandles. */
  inputs: HandleSpec[];
  outputs: HandleSpec[];
  /** zod schéma parametrů uzlu (z registry.ts — typ je tam ZodType). */
  stateful?: { durable: boolean };
  phase: 1 | 2 | 3;
  /** Název lucide ikony (string, ať registr nemusí importovat klient). */
  icon: string;
  labelKey: string;
  descriptionKey: string;
};

/** Výsledek validace grafu. */
export type GraphValidationIssue = {
  code: string;
  message: string;
  /** Id uzlu / hrany, kterého se týká (pro highlight v editoru). */
  nodeId?: string;
  edgeId?: string;
};

export type GraphValidationResult = {
  ok: boolean;
  errors: GraphValidationIssue[];
  warnings: GraphValidationIssue[];
};
